import React, { useState, useEffect } from 'react';
import api from '../api/api';
import {
  Store, Plus, X, Copy, ExternalLink, Edit, Trash2,
  Download, MessageCircle, Search, MapPin, Mail, Phone,
  TrendingUp, Shield, Award, Info, Building, QrCode,
  ChevronLeft, ChevronRight, Power
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';

const Manager = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [selectedStore, setSelectedStore] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [qrStore, setQrStore] = useState(null);
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);

  // --- CAMPAIGN STATE ---
  const [campaignActive, setCampaignActive] = useState(true);
  const [togglingCampaign, setTogglingCampaign] = useState(false);
  const [showCampaignPopup, setShowCampaignPopup] = useState(false);
  const [campaignFeedbackPopup, setCampaignFeedbackPopup] = useState({ show: false, message: '', isSuccess: true }); // <-- NEW STATE FOR SUCCESS/ERROR POPUP

  const [formData, setFormData] = useState({
    type: '',
    designation: '',
    storeName: '',
    shortName: '',
    city: '',
    phoneNumber: '',
    email: '',
    address: '',
    region: '',
    username: '',
    password: '',
    role: 'Store',
    spinUrl: '',
    assignedStoreIds: [],
  });

const baseHostingUrl = window.location.origin;

  const normalizeType = (store) => {
    return (store.type || store.role || store.designation || '').toString().toLowerCase();
  };

  const getTypeLabel = (store) => {
    const kind = normalizeType(store);
    if (kind === 'admin') return 'Admin';
    if (kind === 'user') return 'User';
    if (kind === 'store') return 'Store';
    return (store.type || store.role || store.designation || 'Store');
  };

  const fetchStores = async () => {
    try {
      const res = await api.get('/api/stores/all');
      const storesWithRegionString = res.data.map(store => ({
        ...store,
        region: store.region
          ? (Array.isArray(store.region) ? store.region.join(', ') : store.region)
          : ''
      }));
      setStores(storesWithRegionString);
    } catch (err) {
      console.error('Error loading stores:', err);
    }
  };

  // --- Fetch Campaign Status using the verify API ---
  const fetchCampaignStatus = async () => {
    try {
      const res = await api.get('/api/campaign/verify');
      if (res.data && res.data.isActive !== undefined) {
        const isActiveBool = res.data.isActive === true || res.data.isActive === 1 || res.data.isActive === '1';
        setCampaignActive(isActiveBool);
      }
    } catch (err) {
      console.error('Error loading campaign status:', err);
    }
  };

  useEffect(() => {
    fetchStores();
    fetchCampaignStatus();
  }, []);

  // --- Toggle Campaign Logic ---
  const executeToggleCampaign = async () => {
    setShowCampaignPopup(false); // Close the confirmation popup
    setTogglingCampaign(true);
    try {
      const newStatus = !campaignActive;
      const payloadValue = newStatus ? 1 : 0;
      
      await api.post('/api/campaign/toggle', { active: payloadValue });
      
      setCampaignActive(newStatus);
      
      // Trigger the custom success popup instead of native alert
      setCampaignFeedbackPopup({
        show: true,
        message: `Campaign has been turned ${newStatus ? 'ON' : 'OFF'}`,
        isSuccess: true
      });
    } catch (err) {
      console.error(err);
      // Trigger the custom error popup instead of native alert
      setCampaignFeedbackPopup({
        show: true,
        message: 'Failed to toggle campaign status. Server issue.',
        isSuccess: false
      });
    } finally {
      setTogglingCampaign(false);
    }
  };

  const resetForm = () => {
    setFormData({
      type: '',
      designation: '',
      storeName: '',
      shortName: '',
      city: '',
      phoneNumber: '',
      email: '',
      address: '',
      region: '',
      username: '',
      password: '',
      role: 'Store',
      spinUrl: '',
      assignedStoreIds: [],
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!formData.shortName.trim()) {
      alert('❌ Short Name is required! Please enter a short name.');
      return;
    }

    setLoading(true);

    const payload = {
      type: formData.type,
      role: formData.type,
      designation: formData.type,
      storeName: formData.storeName,
      email: formData.email,
      username: formData.username,
      password: formData.password,
      phoneNumber: formData.phoneNumber,
      shortName: formData.shortName,
    };

    if (formData.type === 'Admin') {
      // no extra fields
    } else if (formData.type === 'User') {
      payload.city = formData.city;
      payload.assignedStoreIds = formData.assignedStoreIds.join(',');
    } else if (formData.type === 'Store') {
      payload.city = formData.city;
      payload.address = formData.address;
      payload.region = formData.region;
      payload.spinUrl = formData.spinUrl;
    }

    try {
      if (editingId) {
        await api.put(`/api/stores/update/${editingId}`, payload);
        alert('Account Updated ✅');
      } else {
        await api.post('/api/stores/assign', payload);
        alert('Account Created ✅');
      }

      setIsModalOpen(false);
      setEditingId(null);
      resetForm();
      fetchStores();
    } catch (err) {
      alert(err.response?.data?.error || 'Server Error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = store => {
    setEditingId(store.id);

    let regionValue = store.region;
    if (Array.isArray(regionValue)) {
      regionValue = regionValue.join(', ');
    } else if (!regionValue) {
      regionValue = '';
    }

    setFormData({
      type: store.type || store.role || store.designation || 'Store',
      designation: store.designation || store.type || store.role || '',
      storeName: store.storeName || '',
      shortName: store.shortName || '',
      city: store.city || '',
      phoneNumber: store.phoneNumber || '',
      email: store.email || '',
      address: store.address || '',
      region: regionValue,
      username: store.username || '',
      password: '',
      role: store.role || 'Store',
      spinUrl: store.spinUrl || '',
      assignedStoreIds: [],
    });

    setIsModalOpen(true);
  };

  const handleDelete = async id => {
    if (window.confirm('Are you sure you want to delete this store?')) {
      try {
        await api.delete(`/api/stores/delete/${id}`);
        fetchStores();
        alert('Deleted successfully');
      } catch (err) {
        alert('Delete Failed');
      }
    }
  };

  const copyToClipboard = text => {
    navigator.clipboard.writeText(text);
    alert('Link copied ✅');
  };

  const downloadQR = (storeId, storeName) => {
    const canvas = document.getElementById(`qr-canvas-${storeId}`);
    if (!canvas) return alert('QR not ready');
    const link = document.createElement('a');
    link.download = `${storeName}_QR.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const shareOnWhatsApp = (storeName, link) => {
    const msg = `🎡 *${storeName}* - Spin & Win!\n\nClick here: ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const handleAssignedStoresChange = (e) => {
    const selected = Array.from(e.target.selectedOptions, opt => opt.value);
    setFormData({ ...formData, assignedStoreIds: selected });
  };

  const selectAllStores = () => {
    const allIds = stores.map(s => s.id.toString());
    setFormData({ ...formData, assignedStoreIds: allIds });
  };

  const handleRowClick = (store) => {
    setSelectedStore(store);
    setIsDetailModalOpen(true);
  };

  const showQRModal = (store, e) => {
    e.stopPropagation();
    setQrStore(store);
    setIsQrModalOpen(true);
  };

  const filteredStores = stores.filter(store =>
    store.storeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    store.shortName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    store.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    store.designation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    store.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    store.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentStores = filteredStores.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredStores.length / itemsPerPage);

  const counts = stores.reduce(
    (acc, s) => {
      const t = normalizeType(s);
      if (t === 'admin') acc.admin += 1;
      else if (t === 'user') acc.user += 1;
      else acc.store += 1;
      return acc;
    },
    { admin: 0, user: 0, store: 0 }
  );

  const inputStyle = 'w-full border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-black bg-white text-sm';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* --- CAMPAIGN TOGGLE --- */}
        <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col md:flex-row justify-between items-center gap-4">
          <div>
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <Power className={campaignActive ? 'text-green-500' : 'text-red-500'} size={20} />
              Global Campaign Status: {campaignActive ? 'ON' : 'OFF'}
            </h2>
            <p className="text-sm text-gray-500">
              Turn this OFF to redirect all customer Spin Wheel links to the "Campaign Ended" page.
            </p>
          </div>
          <button
            onClick={() => setShowCampaignPopup(true)}
            disabled={togglingCampaign}
            className={`px-6 py-2.5 rounded-lg font-bold text-white transition shadow-md flex items-center gap-2 ${
              campaignActive 
                ? 'bg-red-500 hover:bg-red-600' 
                : 'bg-green-500 hover:bg-green-600'
            } ${togglingCampaign ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {togglingCampaign ? 'Updating...' : (campaignActive ? 'Turn Campaign OFF' : 'Turn Campaign ON')}
          </button>
        </div>
        {/* --- END CAMPAIGN TOGGLE --- */}

        <div className="relative overflow-hidden bg-white rounded-2xl shadow-lg mb-8 border border-gray-100">
          <div className="absolute top-0 right-0 w-72 h-72 bg-indigo-100 rounded-full filter blur-3xl opacity-40 -mr-36 -mt-36"></div>
          <div className="relative p-6 md:p-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-4">
                <div className="bg-indigo-600 p-3 rounded-xl shadow-md">
                  <Store className="text-white" size={28} />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-800">Store Management</h1>
                  <p className="text-gray-500 text-sm flex items-center gap-2">
                    <Shield size={14} className="text-indigo-500" /> Manage stores, types & QR codes
                  </p>
                </div>
              </div>
              <button
                onClick={() => { setEditingId(null); resetForm(); setIsModalOpen(true); }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl flex items-center gap-2 shadow-md transition-all font-semibold"
              >
                <Plus size={18} /> Add New Store
              </button>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div className="relative w-full sm:w-80">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search by name, email, city..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-300 outline-none bg-gray-50"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="flex gap-3 flex-wrap">
                <div className="bg-indigo-50 px-4 py-2 rounded-full text-indigo-700 text-sm font-medium flex items-center gap-2">
                  <Store size={14} /> Total: {filteredStores.length}
                </div>
                <div className="bg-purple-50 px-4 py-2 rounded-full text-purple-700 text-sm font-medium flex items-center gap-2">
                  Admin: {counts.admin}
                </div>
                <div className="bg-blue-50 px-4 py-2 rounded-full text-blue-700 text-sm font-medium flex items-center gap-2">
                  User: {counts.user}
                </div>
                <div className="bg-emerald-50 px-4 py-2 rounded-full text-emerald-700 text-sm font-medium flex items-center gap-2">
                  Store: {counts.store}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-5 py-4 text-left font-semibold text-gray-700">Store</th>
                  <th className="px-5 py-4 text-left font-semibold text-gray-700">Location</th>
                  <th className="px-5 py-4 text-left font-semibold text-gray-700">Contact</th>
                  <th className="px-5 py-4 text-center font-semibold text-gray-700">Type</th>
                  <th className="px-5 py-4 text-center font-semibold text-gray-700">QR / Links</th>
                  <th className="px-5 py-4 text-center font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {currentStores.map((store) => {
                  const displayLink = store.spinUrl || `${baseHostingUrl}${store.shortName}?view=customer&storeId=${store.id}`;
                  const regionDisplay = Array.isArray(store.region)
                    ? store.region.join(', ')
                    : (store.region || '-');

                  const typeLabel = getTypeLabel(store);
                  const typeKey = normalizeType(store);

                  return (
                    <tr
                      key={store.id}
                      className="hover:bg-indigo-50/30 transition cursor-pointer"
                      onClick={() => handleRowClick(store)}
                    >
                      <td className="px-5 py-4">
                        <div className="font-semibold text-gray-800">{store.storeName}</div>
                        <div className="text-xs text-indigo-600 font-mono mt-0.5">
                          {store.shortName || '-'}
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1 text-gray-600">
                          <MapPin size={14} />
                          <span>{store.city || '-'}</span>
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {regionDisplay !== '-' ? regionDisplay : ''}
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1 text-gray-600">
                          <Mail size={14} />
                          <span className="truncate max-w-[150px]">{store.email}</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-500 text-xs mt-0.5">
                          <Phone size={12} />
                          {store.phoneNumber || '-'}
                        </div>
                      </td>

                      <td className="px-5 py-4 text-center">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            typeKey === 'admin'
                              ? 'bg-purple-100 text-purple-700'
                              : typeKey === 'user'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-emerald-100 text-emerald-700'
                          }`}
                        >
                          {typeLabel}
                        </span>
                      </td>

                      <td className="px-5 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={(e) => showQRModal(store, e)}
                            className="p-1.5 bg-indigo-100 text-indigo-600 rounded-lg hover:bg-indigo-200 transition"
                            title="View QR"
                          >
                            <QrCode size={16} />
                          </button>
                          <button
                            onClick={() => copyToClipboard(displayLink)}
                            className="p-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition"
                            title="Copy URL"
                          >
                            <Copy size={14} />
                          </button>
                          <button
                            onClick={() => shareOnWhatsApp(store.storeName, displayLink)}
                            className="p-1.5 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition"
                            title="Share"
                          >
                            <MessageCircle size={14} />
                          </button>
                          <a
                            href={displayLink}
                            target="_blank"
                            rel="noreferrer"
                            className="p-1.5 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition"
                            title="Open"
                          >
                            <ExternalLink size={14} />
                          </a>
                        </div>
                        <div className="hidden">
                          <QRCodeCanvas
                            id={`qr-canvas-${store.id}`}
                            value={displayLink}
                            size={128}
                            level="H"
                            includeMargin={true}
                          />
                        </div>
                      </td>

                      <td className="px-5 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleEdit(store)}
                            className="p-1.5 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition"
                            title="Edit"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(store.id)}
                            className="p-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {filteredStores.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center py-16 text-gray-400">
                      <Store className="mx-auto mb-3 text-gray-300" size={48} />
                      No stores found. Click "Add New Store" to create one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredStores.length > 0 && (
            <div className="px-5 py-4 border-t border-gray-100 flex justify-between items-center bg-gray-50">
              <div className="text-sm text-gray-500">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredStores.length)} of {filteredStores.length} stores
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 hover:bg-white transition"
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-lg text-sm">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 hover:bg-white transition"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <div className="bg-indigo-600 p-2 rounded-lg">
                  <Store className="text-white" size={18} />
                </div>
                <h3 className="text-xl font-bold text-gray-800">
                  {editingId ? 'EDIT ACCOUNT' : 'Create New Store'}
                </h3>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-red-500 transition">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className="text-xs font-semibold text-gray-600">Type *</label>
                <select
                  className={inputStyle}
                  required
                  value={formData.type}
                  onChange={e => setFormData({
                    ...formData,
                    type: e.target.value,
                    designation: e.target.value
                  })}
                >
                  <option value="">-- Select Type --</option>
                  <option value="Admin">Admin</option>
                  <option value="User">User</option>
                  <option value="Store">Store</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-600">Designation (Auto)</label>
                <input
                  className={inputStyle}
                  value={formData.designation || formData.type}
                  readOnly
                />
              </div>

              {formData.type === 'Admin' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Name *</label>
                    <input
                      className={inputStyle}
                      required
                      value={formData.storeName}
                      onChange={e => setFormData({ ...formData, storeName: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Short Name (URL) <span className="text-red-500">*</span></label>
                    <input
                      className={inputStyle}
                      required
                      value={formData.shortName}
                      onChange={e => setFormData({ ...formData, shortName: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Email *</label>
                    <input
                      type="email"
                      className={inputStyle}
                      required
                      value={formData.email}
                      onChange={e => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Username *</label>
                    <input
                      className={inputStyle}
                      required
                      value={formData.username}
                      onChange={e => setFormData({ ...formData, username: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Password {!editingId && '*'}</label>
                    <input
                      type="text"
                      className={inputStyle}
                      required={!editingId}
                      value={formData.password}
                      onChange={e => setFormData({ ...formData, password: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Phone</label>
                    <input
                      className={inputStyle}
                      value={formData.phoneNumber}
                      onChange={e => setFormData({ ...formData, phoneNumber: e.target.value })}
                    />
                  </div>
                </div>
              )}

              {formData.type === 'User' && (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="text-xs font-semibold text-gray-600">Name *</label>
                      <input
                        className={inputStyle}
                        required
                        value={formData.storeName}
                        onChange={e => setFormData({ ...formData, storeName: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600">Short Name (URL) <span className="text-red-500">*</span></label>
                      <input
                        className={inputStyle}
                        required
                        value={formData.shortName}
                        onChange={e => setFormData({ ...formData, shortName: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600">Email *</label>
                      <input
                        type="email"
                        className={inputStyle}
                        required
                        value={formData.email}
                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600">Username *</label>
                      <input
                        className={inputStyle}
                        required
                        value={formData.username}
                        onChange={e => setFormData({ ...formData, username: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600">Password {!editingId && '*'}</label>
                      <input
                        type="text"
                        className={inputStyle}
                        required={!editingId}
                        value={formData.password}
                        onChange={e => setFormData({ ...formData, password: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600">Phone</label>
                      <input
                        className={inputStyle}
                        value={formData.phoneNumber}
                        onChange={e => setFormData({ ...formData, phoneNumber: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600">City</label>
                      <input
                        className={inputStyle}
                        value={formData.city}
                        onChange={e => setFormData({ ...formData, city: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-gray-600">Assigned Stores (multi-select)</label>
                    <div className="flex gap-2 mb-1 items-center">
                      <button
                        type="button"
                        onClick={selectAllStores}
                        className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded hover:bg-indigo-200"
                      >
                        Select All
                      </button>
                      <span className="text-xs text-gray-400">Hold Ctrl/Cmd to select multiple</span>
                    </div>
                    <select
                      multiple
                      className={inputStyle + ' h-40'}
                      value={formData.assignedStoreIds}
                      onChange={handleAssignedStoresChange}
                    >
                      {stores.map(store => (
                        <option key={store.id} value={store.id.toString()}>
                          {store.storeName} ({store.city || 'no city'})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {formData.type === 'Store' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Store Name *</label>
                    <input
                      className={inputStyle}
                      required
                      value={formData.storeName}
                      onChange={e => setFormData({ ...formData, storeName: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-gray-600">Short Name (URL) <span className="text-red-500">*</span></label>
                    <input
                      className={inputStyle}
                      required
                      value={formData.shortName}
                      onChange={e => setFormData({ ...formData, shortName: e.target.value })}
                    />
                    <p className="text-xs text-red-400 mt-1">Required for Store</p>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-gray-600">City</label>
                    <input
                      className={inputStyle}
                      value={formData.city}
                      onChange={e => setFormData({ ...formData, city: e.target.value })}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-xs font-semibold text-gray-600">Full Address</label>
                    <textarea
                      className={inputStyle}
                      rows="2"
                      value={formData.address}
                      onChange={e => setFormData({ ...formData, address: e.target.value })}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-xs font-semibold text-gray-600">Region</label>
                    <input
                      type="text"
                      className={inputStyle}
                      placeholder="e.g., North, South, Punjab, Sindh, etc."
                      value={formData.region}
                      onChange={e => setFormData({ ...formData, region: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-gray-600">Email *</label>
                    <input
                      type="email"
                      className={inputStyle}
                      required
                      value={formData.email}
                      onChange={e => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-gray-600">Username *</label>
                    <input
                      className={inputStyle}
                      required
                      value={formData.username}
                      onChange={e => setFormData({ ...formData, username: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-gray-600">Password {!editingId && '*'}</label>
                    <input
                      type="text"
                      className={inputStyle}
                      required={!editingId}
                      value={formData.password}
                      onChange={e => setFormData({ ...formData, password: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-gray-600">Phone</label>
                    <input
                      className={inputStyle}
                      value={formData.phoneNumber}
                      onChange={e => setFormData({ ...formData, phoneNumber: e.target.value })}
                    />
                  </div>
                </div>
              )}

              {!formData.type && (
                <p className="text-gray-400 text-center py-4">Please select a type to continue.</p>
              )}

              {formData.type && (
                <div className="md:col-span-2 mt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full py-3 rounded-xl font-bold text-white transition ${
                      loading ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'
                    }`}
                  >
                    {loading ? 'Saving...' : editingId ? 'Update Account' : 'Create Account'}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {isDetailModalOpen && selectedStore && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <div className="bg-indigo-600 p-2 rounded-lg">
                  <Info className="text-white" size={18} />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Account Details</h3>
              </div>
              <button onClick={() => setIsDetailModalOpen(false)} className="text-gray-400 hover:text-red-500 transition">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-400 flex items-center gap-1"><Building size={12} /> Store Name</p>
                  <p className="font-semibold text-gray-800">{selectedStore.storeName}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-400 flex items-center gap-1"><Award size={12} /> Short ID</p>
                  <p className="font-mono text-indigo-600">{selectedStore.shortName || '-'}</p>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2"><Mail size={16} /> Contact Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-400">Email</p>
                    <p className="text-sm">{selectedStore.email}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-400">Phone</p>
                    <p className="text-sm">{selectedStore.phoneNumber || '-'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-400">Username</p>
                    <p className="text-sm">{selectedStore.username || '-'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-400">Designation</p>
                    <p className="text-sm">{selectedStore.designation || selectedStore.type || '-'}</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2"><MapPin size={16} /> Location Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-400">City</p>
                    <p className="text-sm">{selectedStore.city || '-'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-400">Region</p>
                    <p className="text-sm">
                      {(() => {
                        let region = selectedStore.region;
                        if (Array.isArray(region)) region = region.join(', ');
                        return region || '-';
                      })()}
                    </p>
                  </div>
                </div>
                {selectedStore.address && (
                  <div className="bg-gray-50 rounded-xl p-3 mt-2">
                    <p className="text-xs text-gray-400">Full Address</p>
                    <p className="text-sm">{selectedStore.address}</p>
                  </div>
                )}
              </div>

              <div className="border-t border-gray-100 pt-4">
                <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2"><TrendingUp size={16} /> Account Type</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-400">Type</p>
                    <p className="font-bold text-sm">
                      {selectedStore.type || selectedStore.role || selectedStore.designation || '-'}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-400">Created At</p>
                    <p className="text-sm">
                      {selectedStore.createdAt ? new Date(selectedStore.createdAt).toLocaleString() : '-'}
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-3 mt-2">
                  <p className="text-xs text-gray-400">Spin URL</p>
                  <p className="text-sm text-blue-600 break-all">
                    {selectedStore.spinUrl || `${baseHostingUrl}${selectedStore.shortName}?view=customer&storeId=${selectedStore.id}`}
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">QR Code</h4>
                <div className="flex justify-center">
                  <div className="bg-white p-3 rounded-xl shadow-md border">
                    <QRCodeCanvas
                      value={selectedStore.spinUrl || `${baseHostingUrl}${selectedStore.shortName}?view=customer&storeId=${selectedStore.id}`}
                      size={150}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex justify-end">
              <button onClick={() => setIsDetailModalOpen(false)} className="px-5 py-2 bg-gray-200 rounded-xl hover:bg-gray-300 transition">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {isQrModalOpen && qrStore && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 text-center">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">QR Code - {qrStore.storeName}</h3>
              <button onClick={() => setIsQrModalOpen(false)} className="text-gray-400 hover:text-red-500">
                <X size={24} />
              </button>
            </div>

            <div className="flex justify-center mb-4">
              <QRCodeCanvas
                value={qrStore.spinUrl || `${baseHostingUrl}${qrStore.shortName}?view=customer&storeId=${qrStore.id}`}
                size={200}
                level="H"
                includeMargin={true}
              />
            </div>

            <div className="flex gap-3 justify-center">
              <button
                onClick={() => { downloadQR(qrStore.id, qrStore.storeName); }}
                className="bg-emerald-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-emerald-700"
              >
                <Download size={16} /> Download
              </button>
              <button
                onClick={() => shareOnWhatsApp(qrStore.storeName, qrStore.spinUrl || `${baseHostingUrl}${qrStore.shortName}?view=customer&storeId=${qrStore.id}`)}
                className="bg-[#25D366] text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-[#20bd5a]"
              >
                <MessageCircle size={16} /> Share
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- CAMPAIGN TOGGLE CONFIRMATION POPUP MODAL --- */}
      {showCampaignPopup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 text-center shadow-2xl">
            <div className="flex justify-center mb-4">
              <div className={`p-4 rounded-full ${campaignActive ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                <Power size={32} />
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              Turn Campaign {campaignActive ? 'OFF' : 'ON'}?
            </h3>
            <p className="text-gray-500 mb-6">
              Are you sure you want to turn the global campaign {campaignActive ? 'OFF (Stop)' : 'ON (Start)'}?
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setShowCampaignPopup(false)}
                className="px-5 py-2.5 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 transition font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={executeToggleCampaign}
                className={`px-5 py-2.5 text-white rounded-xl transition font-semibold ${
                  campaignActive ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'
                }`}
              >
                Yes, Turn {campaignActive ? 'OFF' : 'ON'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- NEW CAMPAIGN SUCCESS/ERROR FEEDBACK POPUP MODAL --- */}
      {campaignFeedbackPopup.show && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 text-center shadow-2xl">
            <div className="flex justify-center mb-4">
              <div className={`p-4 rounded-full ${campaignFeedbackPopup.isSuccess ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                <Info size={32} />
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              {campaignFeedbackPopup.isSuccess ? 'Success' : 'Error'}
            </h3>
            <p className="text-gray-500 mb-6">
              {campaignFeedbackPopup.message}
            </p>
            <div className="flex justify-center">
              <button
                onClick={() => setCampaignFeedbackPopup({ show: false, message: '', isSuccess: true })}
                className="px-8 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition font-semibold shadow-md"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Manager;